import { Injectable } from '@angular/core';
import { Observable, from, iif, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  IpInfo,
  LoginByEmailParams,
  LoginByMobileParams,
  LoginByNameParams,
  LoginUser,
  QuickGeneral2faVerifyParams,
  QuickSendEmailParams,
  QuickSendMobileVerifyParams,
  RegisterByEmailParams,
  RegisterByMobileParams,
  RegisterByNameParams,
  SendEmailVerfiyParams,
  SendMobileVerifyParams,
  SocialByPass,
  SocialUserLoginData,
  SocialUserLoginParam,
  SocialUserVerifyParams,
  VerifyAction,
  VerifyEmailBeforeOtpParam,
  VerifyEmailParam,
  VerifyGoogleParam,
  VerifyPhoneParam,
  Web3WalletNonceParams,
  Web3WalletSignatureInfo,
} from '../interfaces/auth.interface';
import { ResponseData } from '../interfaces/response.interface';
import { FpSourceAction } from '../service/fingerprint.service';
import { BaseApi } from './base.api';

@Injectable({
  providedIn: 'root',
})
export class AuthApi extends BaseApi {
  /**
   * 获取api 请求token
   *
   * @param iniToken
   * @returns //
   */
  getSetUpToken(iniToken: string): Observable<ResponseData<string> | null> {
    const url = `${environment.apiUrl}/v1/api/auth/setup?s=${encodeURIComponent(iniToken)}`;
    return this.http.get<ResponseData<string> | null>(url, { headers: { 'ngsw-bypass': 'true' } }).pipe(
      catchError(error => {
        this.sentryService.apiError(url, error);
        return of(null);
      }),
    );
  }

  /**
   * 获取验证码设置Id
   *
   * @returns //
   */
  getCaptchaId(): Observable<string> {
    const url = `${environment.apiUrl}/v1/api/auth/getcaptchaid`;
    return this.get<ResponseData<string>>(url, undefined, undefined, () => of(null)).pipe(
      map(result => result?.data ?? ''),
    );
  }

  /**
   * 用户名密码注册
   *
   * @param param
   * @returns //
   */
  userNameRegister(param: RegisterByNameParams): Observable<ResponseData<string>> {
    this.dataCollectionService.addPoint({ eventId: 30008, actionValue1: 1 });
    const url = `${environment.apiUrl}/v1/member/auth/registerbyuserpwd`;
    return this.generateVisitorId('registerbyuserpwd', () => {
      return this.post<ResponseData<string>>(url, param).pipe(
        tap(res => {
          if (res?.data) {
            // 注册成功，清空邀请码、Affiliate
            this.localStorageService.inviteCode = null;
            this.localStorageService.myAffiliate = null;
          }
        }),
      );
    });
  }

  /**
   * 手机注册
   *
   * @param param
   * @returns //
   */
  mobileRegister(param: RegisterByMobileParams): Observable<ResponseData<string>> {
    this.dataCollectionService.addPoint({ eventId: 30008, actionValue1: 0 });
    const url = `${environment.apiUrl}/v1/member/auth/registerbymobile`;
    return this.generateVisitorId('registerbymobile', () => {
      return this.post<ResponseData<string>>(url, param).pipe(
        tap(res => {
          if (res?.data) {
            // 注册成功，清空邀请码、Affiliate
            this.localStorageService.inviteCode = null;
            this.localStorageService.myAffiliate = null;
          }
        }),
      );
    });
  }

  /**
   * 邮箱注册
   *
   * @param param
   * @returns //
   */
  emailRegister(param: RegisterByEmailParams): Observable<ResponseData<string>> {
    this.dataCollectionService.addPoint({ eventId: 30008, actionValue1: 2 });
    const url = `${environment.apiUrl}/v1/member/auth/registerbyemail`;
    return this.generateVisitorId('registerbyemail', () => {
      return this.post<ResponseData<string>>(url, param).pipe(
        tap(res => {
          if (res?.data) {
            // 注册成功，清空邀请码、Affiliate
            this.localStorageService.inviteCode = null;
            this.localStorageService.myAffiliate = null;
          }
        }),
      );
    });
  }

  /**
   * 用户名登录
   *
   * @param param
   * @returns //
   */
  loginByName(param: LoginByNameParams): Observable<ResponseData<LoginUser>> {
    const url = `${environment.apiUrl}/v1/member/auth/loginbyname`;
    return this.generateVisitorId('loginbyname', () => {
      return this.post(url, param);
    });
  }

  /**
   * 发送手机Otp验证
   *
   * @param param
   * @returns //
   */
  sendMobileVerify(param: SendMobileVerifyParams): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/auth/sendmobileverify`;
    return this.post(url, param);
  }

  /**
   * 发送邮箱Otp验证
   *
   * @param param
   * @returns //
   */
  sendEmailVerify(param: SendEmailVerfiyParams): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/auth/sendemailverify`;
    return this.post(url, param);
  }

  /**
   * 發送Email Otp (使用者不需輸入Email)
   *
   * @param params 参数
   * @returns
   */
  quickSendEmailVerify(params: QuickSendEmailParams): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/auth/quicksendemailverify`;
    return this.post(url, params);
  }

  /**
   *  發送手機Otp (使用者輸入不需手機號碼)
   *
   * @param params 参数
   * @returns
   */
  quickSendMobileVerify(params: QuickSendMobileVerifyParams): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/auth/quicksendmobileverify`;
    return this.post(url, params);
  }

  /**
   * 手机登录
   *
   * @param param
   * @returns //
   */
  loginByMobile(param: LoginByMobileParams): Observable<ResponseData<LoginUser>> {
    const url = `${environment.apiUrl}/v1/member/auth/loginbymobile`;
    return this.generateVisitorId('loginbymobile', () => {
      return this.post(url, param);
    });
  }

  /**
   * 邮箱登录
   *
   * @param param
   * @returns //
   */
  loginByEmail(param: LoginByEmailParams): Observable<ResponseData<LoginUser>> {
    const url = `${environment.apiUrl}/v1/member/auth/loginbyemail`;
    return this.generateVisitorId('loginbyemail', () => {
      return this.post(url, param);
    });
  }

  /**
   * 第三方 绑定登录
   *
   * @param params
   * @returns //
   */
  socialUserLoginByMobile(params: LoginByMobileParams): Observable<ResponseData<LoginUser>> {
    const url = `${environment.apiUrl}/v1/member/auth/socialuserbindloginbymobile`;
    return this.generateVisitorId('socialuserbindloginbymobile', () => {
      return this.post<ResponseData<LoginUser>>(url, params);
    });
  }

  /**
   * 获取MetaMask认证的Nonce
   *
   * @param address 通过WEB3库获取的地址
   * @returns //
   */
  getNonce(address: string): Observable<ResponseData<string>> {
    return this.get<ResponseData<string>>(`${environment.apiUrl}/v1/member/auth/getmetamasknonce?address=${address}`);
  }

  /**
   * 验证第三方登陆是否成功
   *
   * @param param 社交媒体登陆参数
   * autoLogin 下次自动登录
   * credential 回传凭证 (Request.Form["credential"])
   * token 回传令牌 （Request.Form["g_csrf_token"]）
   * cookiesToken Cookie 令牌 （Request.Cookies["g_csrf_token"])
   * address MetaMask地址
   * nonce
   * signature MetaMask签名信息
   * additionalProp
   * code Line 返回的信息
   * state
   * userType 登录方式 0-google 1-telegram 2-metamask 3-line
   * @returns //
   */
  verifyLogin(param: SocialUserLoginParam): Observable<ResponseData<SocialUserLoginData>> {
    const url = `${environment.apiUrl}/v1/member/auth/socialuserlogin`;
    return this.generateVisitorId('socialuserlogin', () => {
      return this.post<ResponseData<SocialUserLoginData>>(url, param);
    });
  }

  /**
   * 2fa验证otp
   *
   * @param uniCode
   * @param param 手机验证或谷歌验证
   * @returns //
   */
  post2faVerify(
    uniCode: string,
    param: VerifyGoogleParam | VerifyPhoneParam | VerifyEmailParam | null,
  ): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/verify2fa`;
    return this.post(url, { uniCode, ...param });
  }

  /**
   *
   * @returns 登出用户
   */
  logout() {
    const url = `${environment.apiUrl}/v1/member/account/logout`;
    return this.post(url);
  }

  /**
   * 修改头像
   *
   * @param params
   * @param params.avatar
   * @returns boolean 或者 null
   */
  modifyAvatar(params: { avatar: string }): Observable<boolean | null> {
    const url = `${environment.apiUrl}/v1/member/account/modifyavatar`;
    return this.post<ResponseData<boolean>>(url, params).pipe(map(x => x?.data || null));
  }

  /**
   * 获取当前IP地址信息
   *
   * @returns //
   */
  getIpInfo(): Observable<IpInfo | null> {
    const url = `${environment.apiUrl}/v1/api/auth/getipinfo`;
    return this.get<ResponseData<IpInfo>>(url).pipe(
      map(x => {
        if (x?.data) {
          if (x.data.ip) {
            this.localStorageService.clientIp = x.data.ip;
            this.sentryService.setUser();
          }
          return x.data;
        }
        return null;
      }),
    );
  }

  /**
   * 第三方验证后跳过 绑定和注册
   *
   * @param params 第三方验证后直接登录的参数
   * @returns //
   */
  onSoicalByPass(params: SocialByPass): Observable<ResponseData<any>> {
    const url = `${environment.apiUrl}/v1/member/auth/registerbysocial`;
    return this.post<ResponseData<any>>(url, params);
  }

  /**
   * 用户安全中心绑定 社交媒体
   *
   * @param params 与社交媒体登录参数保持一致
   * @returns boolean 值
   */
  socialUserBind(params: SocialUserLoginParam): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/account/memberbindsocialuser`;
    return this.post<ResponseData<boolean>>(url, params);
  }

  m8SocialUserBind(params: SocialUserLoginParam): Observable<ResponseData<boolean>> {
    const url = `${environment.apiUrl}/v1/member/account/pubsMemberBindSocialUser`;
    return this.post<ResponseData<boolean>>(url, params);
  }

  /**
   * 前置判断并尝试生成本地收费指纹，无论成功失败，都会继续执行业务
   *
   * @param action 觸發識別的端點名，進指紋 tag 供後台按端點拆用量
   * @param cb 用函数包裹的 原本的业务请求
   * @returns 原请求
   */
  generateVisitorId<T>(action: FpSourceAction, cb: () => T): T {
    if (!this.localStorageService.visitorIdPro) {
      // 指纹请求，始终会有返回（10秒超时），成功会返回字符串，不成功会返回null之类
      const fpobs = from(this.fpService.identify(action)).pipe(
        map(x => x?.visitorId),
        catchError(() => {
          return of(null);
        }),
      );
      // 发起指纹请求，并在完成后（无论成功或失败）转到传入oobs请求
      return fpobs.pipe(
        switchMap(visitorId => {
          if (visitorId) this.localStorageService.visitorIdPro = visitorId;
          return cb() as Observable<unknown>;
        }),
      ) as T;
    } else {
      // 无需请求指纹，直接转到传入oobs请求
      return cb();
    }
  }

  /**
   * 驗證從社群平台登入是否成功
   *
   * telegram mini app
   *
   * @param param 社群平台登入驗證參數
   * @returns //
   */
  socialUserVerify(param: SocialUserVerifyParams): Observable<ResponseData<SocialUserLoginData>> {
    const url = `${environment.apiUrl}/v1/member/auth/socialuserverify`;
    return this.generateVisitorId('socialuserverify', () => {
      return this.post<ResponseData<SocialUserLoginData>>(url, param);
    });
  }

  /**
   * 取得Web3錢包隨機數，傳給Web3錢包用來生成簽章
   *
   * @param params
   * @returns // 要傳給Web3錢包簽章的隨機碼，例如：a8a33593-fa1e-4b0f-a712-57a4321b713f
   */
  getWeb3WalletNonce(params: Web3WalletNonceParams): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/getweb3walletnonce`;
    return this.get<ResponseData<string>>(url, params);
  }

  /**
   * Web3錢包登入
   *
   * @param params Web3 錢包登入參數
   * @returns //
   */
  web3WalletLogin(params: Web3WalletSignatureInfo): Observable<ResponseData<SocialUserLoginData>> {
    const url = `${environment.apiUrl}/v1/member/auth/web3walletlogin`;
    return this.post<ResponseData<SocialUserLoginData>>(url, params).pipe(
      switchMap(res =>
        iif(
          () => !!res?.success,
          of(res),
          throwError(() => res),
        ),
      ),
    );
  }

  /**
   * 2fa验证otp
   *
   * @param uniCode
   * @param verifyAction
   * @param param 手机验证或谷歌验证
   * @returns
   */
  general2faVerify(
    verifyAction: VerifyAction,
    param: VerifyGoogleParam | VerifyPhoneParam | VerifyEmailParam | null,
  ): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/general2faverify`;
    return this.post(url, { verifyAction, ...param });
  }

  /**
   * 通用2Fa验证 (使用者不需輸入Email或Mobile)
   *
   * @param param
   * @returns //
   */
  quickGeneral2faVerify(param: QuickGeneral2faVerifyParams): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/quickgeneral2faverify`;
    return this.post(url, param);
  }

  /**
   * 验证邮箱
   *
   * @param param
   * @returns //
   */
  verifyEmailBeforeOtp(param: VerifyEmailBeforeOtpParam): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/verifyemail`;
    return this.post(url, param);
  }

  /**
   * 檢查 IP 是否被允許訪問
   *
   * 帶 tenant 讓尚未取得 token 的首次檢查也能套用商戶的封鎖規則；有 token 時後端以 token 為準
   */
  checkIpAllowed(): Observable<ResponseData<string>> {
    const url = `${environment.apiUrl}/v1/member/auth/isipallowed`;
    return this.get(url, { tenant: environment.common.tenant });
  }
}
